import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../api/AuthApi.services";
import "./OrderDetail.css";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const OrderDetail = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchOrderDetails() {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const headers = { "Content-Type": "application/json" };

        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetchWithAuth(
          `${API_BASE_URL}/order/orders/${orderId}`,
          { method: "GET", headers }
        );

        if (response.ok) {
          const data = await response.json();
          setOrder(data);
          fetchProductDetails(data.details);
        } else {
          throw new Error("Failed to fetch order details.");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    async function fetchProductDetails(orderItems) {
      const headers = { "Content-Type": "application/json" };

      const productRequests = orderItems.map((item) =>
        fetchWithAuth(`${API_BASE_URL}/product/products/${item.product}`, {
          method: "GET",
          headers,
        }).then((res) => (res.ok ? res.json() : null))
      );

      const productData = await Promise.all(productRequests);

      const productMap = {};
      orderItems.forEach((item, index) => {
        productMap[item.product] = productData[index];
      });

      setProducts(productMap);
    }

    fetchOrderDetails();
  }, [orderId]);

  const handleTrackOrder = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await fetchWithAuth(
        `${API_BASE_URL}/order/order_tracking/?order_id=${order.order_id}`,
        { method: "GET", headers }
      );

      if (response.ok) {
        const data = await response.json();
        setTrackingData(data);
        setShowPopup(true);
      } else {
        throw new Error("Failed to fetch tracking data.");
      }
    } catch (err) {
      console.error("Error tracking order:", err.message);
      setTrackingData({ error: err.message });
      setShowPopup(true);
    }
  };

  const handleBack = () => {
    navigate("/orders");
  };

  if (loading) return <p>Loading order details...</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="order-detail mt-5">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
          padding: "10px",
          border: "1px solid #ddd",
          borderRadius: "5px",
          backgroundColor: "#fff",
        }}
      >
        <h2 className="order-title" style={{ margin: "0 20px 0 10px" }}>
          Order Details
        </h2>
        <button
          onClick={handleBack}
          style={{
            backgroundColor: "transparent",
            border: "2px solid #ff9900",
            color: "#ff9900",
            padding: "5px 10px",
            fontSize: "14px",
            fontWeight: "bold",
            borderRadius: "5px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            width: "auto",
          }}
        >
          <span>←</span> Back
        </button>
      </div>

      <div className="order-info">
        <div className="order-info-item">
          <span className="info-label">Order ID:</span>
          <span className="info-value">{order.order_id}</span>
        </div>
        <div className="order-info-item">
          <span className="info-label">Order Date:</span>
          <span className="info-value">
            {new Date(order.order_date).toLocaleDateString()}
          </span>
        </div>
        <div className="order-info-item">
          <span className="info-label">Status:</span>
          <span className={`order-status ${order.order_status.toLowerCase()}`}>
            {order.order_status}
          </span>
        </div>

        <div className="divider"></div>

        <div className="section-title">Items:</div>
        {order.details.length > 0 ? (
          <div className="order-items-container">
            <table className="order-items-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Picture</th>
                  <th>Name</th>
                  <th>Quantity</th>
                  <th>Total Price</th>
                </tr>
              </thead>
              <tbody>
                {order.details.map((item, index) => {
                  const product = products[item.product];
                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <img
                          src={
                            product?.documents?.[0]?.picture ||
                            "https://via.placeholder.com/100"
                          }
                          alt={product?.name || "Product"}
                          className="product-image-order"
                        />
                      </td>
                      <td>{product?.name || "Unknown"}</td>
                      <td>{item.quantity}</td>
                      <td>
                        ₹
                        {(
                          parseFloat(item.unit_price) * item.quantity
                        ).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No items available for this order.</p>
        )}
      </div>

      <div className="order-actions">
        <button onClick={handleTrackOrder} className="track-order-btn">
          Track Order
        </button>
      </div>

      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            color: "#000",
            padding: "20px",
            borderRadius: "10px",
            width: "90%",
            maxWidth: "500px",
            zIndex: 1000,
            boxShadow: "0 0 10px rgba(0,0,0,0.5)",
          }}
        >
          <h3>Order Tracking</h3>
          {trackingData?.error ? (
            <p>Error: {trackingData.error}</p>
          ) : (
            <>
              <p>
                <strong>Status:</strong> {trackingData?.order_status}
              </p>
              <p>
                <strong>Shipment Status:</strong> {trackingData?.shipment_status}
              </p>
              <div>
                <h4>Tracking Events:</h4>
                <ul>
                  {trackingData?.tracking_events?.length > 0 ? (
                    trackingData.tracking_events.map((event, index) => (
                      <li key={index}>
                        {event.timestamp} - {event.status}
                      </li>
                    ))
                  ) : (
                    <li>No tracking events yet.</li>
                  )}
                </ul>
              </div>
            </>
          )}
          <button
            onClick={() => setShowPopup(false)}
            style={{
              marginTop: "10px",
              backgroundColor: "#ff9900",
              color: "#fff",
              padding: "5px 10px",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
